import React, {Fragment} from 'react';
import {AlertTitle, Button, Stack, Typography} from '@mui/material';

import {AlertModal, Snackbar} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';

import {useAutoPaymentCard} from './hooks/useAutoPaymentCard';
import {
	AutoPaymentCardContainer,
	DeactivateButton,
	ErrorAlert,
	IconWrapper,
	ModalDescription,
} from './styles';
import type {AutoPaymentCardProps} from './types';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useHostName} from 'presentation/hooks';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import {FeaturesEnum} from 'presentation/constants/featuresFlagEnum';

const {autoPayment, enable} = translations['pt-br'].privateFlow.myFinancial.openBills;
const {change} = translations['pt-br'].commons;
const {deactivate, debitAccountWarningModal, errorTooltip} =
	translations['pt-br'].privateFlow.myFinancial.openBills.autoPaymentCard;

function AutoPaymentCard({
	type,
	onEnable,
	onChange,
	onDeactivate,
	isEnableLoading,
	isChangeLoading,
	isDeactivateLoading,
}: AutoPaymentCardProps) {
	const {
		isDisabled,
		isCreditCard,
		isDebitAccount,
		isOdontoRedirect,
		isMobile,
		label,
		iconName,
		iconColor,
		snackPosition,
		debitWarningOpen,
		errorTooltipOpen,
		resolvedEnableLoading,
		resolvedChangeLoading,
		resolvedDeactivateLoading,
		handleChange,
		handleEnable,
		handleDeactivate,
		setDebitWarningOpen,
		setErrorTooltipOpen,
	} = useAutoPaymentCard({
		type,
		onEnable,
		onChange,
		onDeactivate,
		isEnableLoading,
		isChangeLoading,
		isDeactivateLoading,
	});

	const {isEnabled: isRecurrenceEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_BILLS_BEMOBI_RECURRENCE,
	);
	const {isEnabled: isRecurrencePPOFlag} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_BILLS_BEMOBI_RECURRENCE_PPO,
	);
	const {isEnabled: isRecurrenceOdontoEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_BILLS_BEMOBI_RECURRENCE_ODONTO,
	);
	const {isPPOPlan, odontoOnly} = useAccountInformation();
	const {isGndiSaoPaulo, isNotredameSaude} = useHostName();
	const shouldHideButtonFor =
		isRecurrencePPOFlag && ((isGndiSaoPaulo && isPPOPlan) || isNotredameSaude);

	const hideEnableButton = shouldHideButtonFor || (odontoOnly && !isRecurrenceOdontoEnabled);

	const isCreditCardDesktop = isCreditCard && !isMobile;
	const isCreditCardMobile = isCreditCard && isMobile;
	const isRecurrenceEnabledAndCreditCardMobile = isRecurrenceEnabled && isCreditCardMobile;
	const renderActions = () => {
		if (isOdontoRedirect) {
			if (isDisabled) {
				if (hideEnableButton) return null;
				return (
					<Button variant="outlined" onClick={handleEnable}>
						{enable}
					</Button>
				);
			}
			return (
				<Fragment>
					<Button variant="outlined" onClick={handleChange}>
						{change}
					</Button>
					{isCreditCardDesktop ? (
						<DeactivateButton variant="text" onClick={handleDeactivate}>
							{deactivate}
						</DeactivateButton>
					) : null}
				</Fragment>
			);
		}
		if (!isRecurrenceEnabled) {
			if (!isDebitAccount) return null;
			return (
				<Button
					variant="outlined"
					disabled={resolvedChangeLoading}
					endIcon={resolvedChangeLoading ? <Loading size={16} color="inherit" /> : null}
					onClick={handleChange}>
					{change}
				</Button>
			);
		}
		if (isDisabled) {
			if (hideEnableButton) return null;
			return (
				<Button
					variant="outlined"
					disabled={resolvedEnableLoading}
					endIcon={resolvedEnableLoading ? <Loading size={16} color="inherit" /> : null}
					onClick={handleEnable}>
					{enable}
				</Button>
			);
		}
		return (
			<Fragment>
				<Button
					variant="outlined"
					disabled={resolvedChangeLoading}
					endIcon={resolvedChangeLoading ? <Loading size={16} color="inherit" /> : null}
					onClick={handleChange}>
					{change}
				</Button>
				{isCreditCardDesktop ? (
					<DeactivateButton
						variant="text"
						disabled={resolvedDeactivateLoading}
						endIcon={
							resolvedDeactivateLoading ? <Loading size={16} color="inherit" /> : null
						}
						onClick={handleDeactivate}>
						{deactivate}
					</DeactivateButton>
				) : null}
			</Fragment>
		);
	};

	return (
		<Fragment>
			<AutoPaymentCardContainer id="open-bills-auto-payment-card">
				<Stack gap={1} width="100%">
					<Typography variant="caption" color="text.secondary">
						{autoPayment}
					</Typography>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						gap={1}>
						<Stack direction="row" alignItems="center" gap={1}>
							<IconWrapper>
								<Icon name={iconName} size={20} color={iconColor} />
							</IconWrapper>
							<Typography variant="body1">{label}</Typography>
						</Stack>
						<Stack direction="row" alignItems="center" gap={1}>
							{renderActions()}
						</Stack>
					</Stack>
					{isRecurrenceEnabledAndCreditCardMobile ? (
						<Stack alignItems="center">
							<DeactivateButton
								variant="text"
								disabled={resolvedDeactivateLoading}
								endIcon={
									resolvedDeactivateLoading ? (
										<Loading size={16} color="inherit" />
									) : null
								}
								onClick={handleDeactivate}>
								{deactivate}
							</DeactivateButton>
						</Stack>
					) : null}
				</Stack>
			</AutoPaymentCardContainer>
			{errorTooltipOpen ? (
				<Snackbar
					anchorOrigin={snackPosition}
					open={errorTooltipOpen}
					onClose={() => setErrorTooltipOpen(false)}
					autoHideDuration={6000}>
					<ErrorAlert severity="error">
						<AlertTitle>{errorTooltip.title}</AlertTitle>
						{errorTooltip.description}
					</ErrorAlert>
				</Snackbar>
			) : null}
			<AlertModal
				variant="info"
				customMaxWidth={328}
				headerTitle={debitAccountWarningModal.title}
				description={
					<ModalDescription>{debitAccountWarningModal.description}</ModalDescription>
				}
				showIcon
				open={debitWarningOpen}
				onClose={() => setDebitWarningOpen(false)}
				footerContent={
					<Button
						fullWidth
						size="large"
						variant="contained"
						onClick={() => setDebitWarningOpen(false)}>
						{debitAccountWarningModal.confirm}
					</Button>
				}
			/>
		</Fragment>
	);
}

export {AutoPaymentCard};
